import React, { useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { EmptyState, ErrorState, LoadingState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { fetchPayments } from '../../../../services/request/CustomerPortal';
import { formatDate, inr } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalPayments() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const { t, term } = useTranslation();
  const payments = useAsync(() => fetchPayments(page, size), [page, size]);
  const items = payments.data?.items ?? [];

  return (
    <Box>
      <PageTitle kicker={t('portal.payments.kicker')} title={t('portal.payments.title')} subtitle={t('portal.payments.subtitle')} />
      {payments.error ? <ErrorState message={payments.error} onRetry={payments.reload} /> : null}
      {payments.loading && !payments.data ? <LoadingState label={t('portal.payments.loading')} /> : null}
      {payments.data && payments.data.totalCount === 0 ? <EmptyState title={t('portal.payments.empty')} /> : null}
      {payments.data && payments.data.totalCount > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('portal.common.columns.date')}</TableCell>
                <TableCell>{t('portal.common.columns.type')}</TableCell>
                <TableCell align="right">{t('portal.common.columns.amount')}</TableCell>
                <TableCell>{t('portal.common.columns.status')}</TableCell>
                <TableCell>{t('portal.common.columns.receivedBy')}</TableCell>
                <TableCell>{t('portal.common.columns.note')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(payment => (
                <TableRow key={payment.id} hover>
                  <TableCell>{formatDate(payment.createdDate) ?? '—'}</TableCell>
                  <TableCell>{term('paymentType', payment.paymentType, t('portal.common.payment'))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {inr(payment.amount)}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={payment.status ?? 'RECORDED'} />
                  </TableCell>
                  <TableCell>{payment.paymentReceivedBy || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {payment.note || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={payments.data.totalCount}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('portal.common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('portal.common.rowsRange', { from, to, count })}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={event => {
              setSize(Number(event.target.value));
              setPage(0);
            }}
          />
        </TableContainer>
      ) : null}
    </Box>
  );
}
